import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, User, Package, Check, ChevronRight, ChevronLeft, CreditCard, Truck, Info, AlertCircle, FileText, ChevronUp, ChevronDown, X, Navigation, Clock, Phone, ExternalLink } from 'lucide-react';
import { Button } from './ui/Button';
// Types
type PackageType = 'documents' | 'electronics' | 'clothing' | 'food' | 'other';
type WeightUnit = 'lbs' | 'kg';
interface DropOffLocation {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  hours: string;
  lat: number;
  lng: number;
}
interface FormData {
  pickupLocation: string;
  senderName: string;
  senderPhone: string;
  senderEmail: string;
  recipientName: string;
  recipientPhone: string;
  recipientEmail: string;
  deliveryAddress: string;
  alternatePhone: string;
  packageType: PackageType;
  weight: string;
  weightUnit: WeightUnit;
  length: string;
  width: string;
  height: string;
  quantity: string;
  value: string;
  specialInstructions: string;
  needsInterstate: boolean;
  destinationState: string;
}
interface ShippingFormProps {
  onProceedToPayment?: (data: {
    total: number;
    packageCount: number;
    route: string;
    packageType: string;
  }) => void;
}
const DROP_OFF_LOCATIONS: DropOffLocation[] = [{
  id: 'ny',
  name: 'TaskRunner New York',
  address: '123 Main Street, Suite 100',
  city: 'New York',
  state: 'NY',
  zip: '10001',
  phone: '(212) 555-0123',
  hours: 'Mon-Fri: 9AM-6PM, Sat: 10AM-4PM',
  lat: 40.7128,
  lng: -74.006
}, {
  id: 'la',
  name: 'TaskRunner Los Angeles',
  address: '456 Oak Avenue, Unit 200',
  city: 'Los Angeles',
  state: 'CA',
  zip: '90001',
  phone: '(310) 555-0456',
  hours: 'Mon-Fri: 9AM-6PM, Sat: 10AM-4PM',
  lat: 34.0522,
  lng: -118.2437
}, {
  id: 'houston',
  name: 'TaskRunner Houston',
  address: '789 Pine Road, Building B',
  city: 'Houston',
  state: 'TX',
  zip: '77001',
  phone: '(713) 555-0789',
  hours: 'Mon-Fri: 9AM-6PM, Sat: 10AM-4PM',
  lat: 29.7604,
  lng: -95.3698
}, {
  id: 'atlanta',
  name: 'TaskRunner Atlanta',
  address: '321 Peach Street, Floor 1',
  city: 'Atlanta',
  state: 'GA',
  zip: '30301',
  phone: '(404) 555-0321',
  hours: 'Mon-Fri: 9AM-6PM, Sat: 10AM-4PM',
  lat: 33.749,
  lng: -84.388
}];
const INITIAL_DATA: FormData = {
  pickupLocation: '',
  senderName: '',
  senderPhone: '',
  senderEmail: '',
  recipientName: '',
  recipientPhone: '',
  recipientEmail: '',
  deliveryAddress: '',
  alternatePhone: '',
  packageType: 'documents',
  weight: '',
  weightUnit: 'lbs',
  length: '',
  width: '',
  height: '',
  quantity: '1',
  value: '',
  specialInstructions: '',
  needsInterstate: false,
  destinationState: ''
};
const BASE_RATES: Record<PackageType, number> = {
  documents: 25,
  electronics: 45,
  clothing: 35,
  food: 40,
  other: 30
};
const NIGERIAN_STATES = ['Abuja', 'Rivers', 'Kano', 'Kaduna', 'Oyo', 'Enugu', 'Delta', 'Anambra', 'Ogun', 'Edo'];
// Calculate distance between two points using Haversine formula
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
export function ShippingForm({
  onProceedToPayment
}: ShippingFormProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(INITIAL_DATA);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [mobileCalcExpanded, setMobileCalcExpanded] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [costs, setCosts] = useState({
    base: 0,
    weight: 0,
    surcharge: 0,
    interstate: 0,
    subtotal: 0,
    total: 0
  });
  useEffect(() => {
    const quantity = parseInt(formData.quantity) || 1;
    const baseRate = BASE_RATES[formData.packageType] * quantity;
    let weightInLbs = parseFloat(formData.weight) || 0;
    if (formData.weightUnit === 'kg') {
      weightInLbs = weightInLbs * 2.20462;
    }
    const weightCost = weightInLbs * 2.5 * quantity;
    const l = parseFloat(formData.length) || 0;
    const w = parseFloat(formData.width) || 0;
    const h = parseFloat(formData.height) || 0;
    const isOversized = l > 20 || w > 20 || h > 20;
    const surcharge = isOversized ? 15 * quantity : 0;
    const interstateCost = formData.needsInterstate ? 30 * quantity : 0;
    const subtotal = baseRate + weightCost + surcharge + interstateCost;
    setCosts({
      base: baseRate,
      weight: weightCost,
      surcharge,
      interstate: interstateCost,
      subtotal,
      total: subtotal
    });
  }, [formData]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleUnitToggle = (unit: WeightUnit) => {
    setFormData(prev => ({
      ...prev,
      weightUnit: unit
    }));
  };
  const handleFindClosestLocation = () => {
    setShowLocationModal(true);
    setLocationError(null);
    if (navigator.geolocation) {
      setIsLocating(true);
      navigator.geolocation.getCurrentPosition(position => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setIsLocating(false);
      }, error => {
        setIsLocating(false);
        setLocationError('Unable to get your location. Showing all locations.');
      }, {
        timeout: 10000
      });
    }
  };
  const handleSelectLocation = (locationId: string) => {
    setFormData(prev => ({
      ...prev,
      pickupLocation: locationId
    }));
    setShowLocationModal(false);
  };
  const getLocationsWithDistance = () => {
    if (!userLocation) return DROP_OFF_LOCATIONS.map(loc => ({
      ...loc,
      distance: null
    }));
    return DROP_OFF_LOCATIONS.map(loc => ({
      ...loc,
      distance: calculateDistance(userLocation.lat, userLocation.lng, loc.lat, loc.lng)
    })).sort((a, b) => (a.distance || 0) - (b.distance || 0));
  };
  const nextStep = () => setStep(prev => Math.min(prev + 1, 4));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));
  const handleProceedToPayment = () => {
    const location = DROP_OFF_LOCATIONS.find(l => l.id === formData.pickupLocation);
    const locationCity = location ? `${location.city}, ${location.state}` : 'USA';
    onProceedToPayment?.({
      total: costs.total,
      packageCount: parseInt(formData.quantity) || 1,
      route: `${locationCity} → Lagos, Nigeria`,
      packageType: formData.packageType.charAt(0).toUpperCase() + formData.packageType.slice(1)
    });
  };
  const getSelectedLocationName = () => {
    const location = DROP_OFF_LOCATIONS.find(l => l.id === formData.pickupLocation);
    return location ? `${location.name} - ${location.city}, ${location.state}` : '';
  };
  const renderStepIndicator = () => {
    const steps = [{
      id: 1,
      label: 'Sender',
      icon: User
    }, {
      id: 2,
      label: 'Recipient',
      icon: MapPin
    }, {
      id: 3,
      label: 'Package',
      icon: Package
    }, {
      id: 4,
      label: 'Review',
      icon: FileText
    }];
    return <div className="mb-6 sm:mb-8" data-id="element-901">
        <div className="flex items-center justify-between relative" data-id="element-902">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-200 -z-10 rounded-full" data-id="element-903"></div>
          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-brand transition-all duration-300 rounded-full -z-10" style={{
          width: `${(step - 1) / 3 * 100}%`
        }} data-id="element-904"></div>

          {steps.map(s => {
          const isCompleted = step > s.id;
          const isCurrent = step === s.id;
          return <button key={s.id} onClick={() => isCompleted && setStep(s.id)} disabled={!isCompleted} className={`flex flex-col items-center gap-1 sm:gap-2 group ${!isCompleted ? 'cursor-default' : ''}`} data-id="element-905">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 bg-white
                    ${isCompleted ? 'border-success bg-success text-white' : ''}
                    ${isCurrent ? 'border-brand text-brand ring-2 sm:ring-4 ring-brand-100' : ''}
                    ${!isCompleted && !isCurrent ? 'border-gray-300 text-gray-400' : ''}
                  `} data-id="element-906">
                  {isCompleted ? <Check className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-907" /> : <s.icon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-908" />}
                </div>
                <span className={`text-[10px] sm:text-xs font-medium ${isCurrent ? 'text-brand' : 'text-gray-500'}`} data-id="element-909">
                  {s.label}
                </span>
              </button>;
        })}
        </div>
      </div>;
  };
  // Mobile sticky price bar
  const MobilePriceBar = () => <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40" data-id="element-910">
      <button onClick={() => setMobileCalcExpanded(!mobileCalcExpanded)} className="w-full px-4 py-3 flex items-center justify-between" data-id="element-911">
        <div className="flex items-center gap-3" data-id="element-912">
          <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center" data-id="element-913">
            <CreditCard className="w-5 h-5 text-brand" data-id="element-914" />
          </div>
          <div className="text-left" data-id="element-915">
            <span className="text-xs text-gray-500 block" data-id="element-916">Estimated Total</span>
            <span className="text-xl font-bold text-brand" data-id="element-917">
              ${costs.total.toFixed(2)}
            </span>
          </div>
        </div>
        {mobileCalcExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-918" /> : <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-919" />}
      </button>

      <AnimatePresence data-id="element-920">
        {mobileCalcExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden border-t border-gray-100" data-id="element-921">
            <div className="px-4 py-4 space-y-3 bg-gray-50" data-id="element-922">
              <div className="flex justify-between text-sm" data-id="element-923">
                <span className="text-gray-600" data-id="element-924">
                  Base Rate ({formData.packageType})
                </span>
                <span className="font-medium" data-id="element-925">${costs.base.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-926">
                <span className="text-gray-600" data-id="element-927">Weight Charge</span>
                <span className="font-medium" data-id="element-928">${costs.weight.toFixed(2)}</span>
              </div>
              {costs.surcharge > 0 && <div className="flex justify-between text-sm text-warning" data-id="element-929">
                  <span className="flex items-center gap-1" data-id="element-930">
                    <AlertCircle className="w-3 h-3" data-id="element-931" /> Oversize
                  </span>
                  <span className="font-medium" data-id="element-932">
                    ${costs.surcharge.toFixed(2)}
                  </span>
                </div>}
              {costs.interstate > 0 && <div className="flex justify-between text-sm" data-id="element-933">
                  <span className="text-gray-600" data-id="element-934">Interstate</span>
                  <span className="font-medium" data-id="element-935">
                    ${costs.interstate.toFixed(2)}
                  </span>
                </div>}
              <div className="pt-3 border-t border-gray-200 flex justify-between" data-id="element-936">
                <span className="font-bold text-gray-900" data-id="element-937">Total</span>
                <span className="font-bold text-brand text-lg" data-id="element-938">
                  ${costs.total.toFixed(2)}
                </span>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
  // Location Modal
  const LocationModal = () => <AnimatePresence data-id="element-939">
      {showLocationModal && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowLocationModal(false)} data-id="element-940">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-941">
            {/* Modal Header */}
            <div className="p-4 sm:p-6 border-b border-gray-200" data-id="element-942">
              <div className="flex items-center justify-between" data-id="element-943">
                <div data-id="element-944">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-945">
                    Find a Drop-off Location
                  </h3>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-946">
                    Select a location to drop off your package
                  </p>
                </div>
                <button onClick={() => setShowLocationModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-947">
                  <X className="w-5 h-5 text-gray-500" data-id="element-948" />
                </button>
              </div>

              {isLocating && <div className="mt-4 flex items-center gap-2 text-sm text-brand" data-id="element-949">
                  <div className="w-4 h-4 border-2 border-brand border-t-transparent rounded-full animate-spin" data-id="element-950"></div>
                  Finding your location...
                </div>}

              {locationError && <div className="mt-4 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg" data-id="element-951">
                  {locationError}
                </div>}

              {userLocation && !isLocating && <div className="mt-4 flex items-center gap-2 text-sm text-green-600 bg-green-50 p-3 rounded-lg" data-id="element-952">
                  <Navigation className="w-4 h-4" data-id="element-953" />
                  Showing locations nearest to you
                </div>}
            </div>

            {/* Location List */}
            <div className="overflow-y-auto max-h-[50vh]" data-id="element-954">
              {getLocationsWithDistance().map(location => <button key={location.id} onClick={() => handleSelectLocation(location.id)} className={`w-full p-4 sm:p-5 border-b border-gray-100 text-left hover:bg-brand-50 transition-colors ${formData.pickupLocation === location.id ? 'bg-brand-50 border-l-4 border-l-brand' : ''}`} data-id="element-955">
                  <div className="flex items-start justify-between gap-4" data-id="element-956">
                    <div className="flex-1" data-id="element-957">
                      <div className="flex items-center gap-2" data-id="element-958">
                        <h4 className="font-semibold text-gray-900" data-id="element-959">
                          {location.name}
                        </h4>
                        {formData.pickupLocation === location.id && <span className="px-2 py-0.5 bg-brand text-white text-xs rounded-full" data-id="element-960">
                            Selected
                          </span>}
                      </div>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-961">
                        {location.address}
                        <br data-id="element-962" />
                        {location.city}, {location.state} {location.zip}
                      </p>
                      <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500" data-id="element-963">
                        <span className="flex items-center gap-1" data-id="element-964">
                          <Phone className="w-3 h-3" data-id="element-965" />
                          {location.phone}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-966">
                          <Clock className="w-3 h-3" data-id="element-967" />
                          {location.hours}
                        </span>
                      </div>
                    </div>
                    {location.distance !== null && <div className="text-right flex-shrink-0" data-id="element-968">
                        <span className="text-lg font-bold text-brand" data-id="element-969">
                          {location.distance.toFixed(0)}
                        </span>
                        <span className="text-xs text-gray-500 block" data-id="element-970">
                          miles
                        </span>
                      </div>}
                  </div>
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${location.address}, ${location.city}, ${location.state} ${location.zip}`)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 mt-3 text-xs text-brand hover:underline" data-id="element-971">
                    <ExternalLink className="w-3 h-3" data-id="element-972" />
                    Get directions
                  </a>
                </button>)}
            </div>

            {/* Modal Footer */}
            <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50" data-id="element-973">
              <Button fullWidth onClick={() => setShowLocationModal(false)} disabled={!formData.pickupLocation} data-id="element-974">
                {formData.pickupLocation ? 'Confirm Selection' : 'Select a Location'}
              </Button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
  return <>
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-12 pb-24 lg:pb-12" data-id="element-975">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-976">
          {/* LEFT COLUMN - FORM */}
          <div className="lg:col-span-2" data-id="element-977">
            {renderStepIndicator()}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8" data-id="element-978">
              <AnimatePresence mode="wait" data-id="element-979">
                {/* STEP 1: SENDER */}
                {step === 1 && <motion.div key="step1" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4 sm:space-y-6" data-id="element-980">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6" data-id="element-981">
                      Sender Information
                    </h2>

                    <div className="space-y-3 sm:space-y-4" data-id="element-982">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-983">
                        Drop-off Location
                      </label>
                      <p className="text-xs text-gray-500 -mt-2" data-id="element-984">
                        Select the TaskRunner location where you'll drop off
                        your package
                      </p>
                      <select name="pickupLocation" value={formData.pickupLocation} onChange={handleInputChange} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" data-id="element-985">
                        <option value="" className="text-gray-500" data-id="element-986">
                          Select a drop-off location...
                        </option>
                        {DROP_OFF_LOCATIONS.map(location => <option key={location.id} value={location.id} className="text-gray-900" data-id="element-987">
                            {location.name} - {location.city}, {location.state}
                          </option>)}
                      </select>

                      <Button variant="outline" size="sm" className="gap-2 w-full sm:w-auto" onClick={handleFindClosestLocation} data-id="element-988">
                        <Navigation className="w-4 h-4" data-id="element-989" />
                        Find closest location
                      </Button>
                    </div>

                    <div className="space-y-4" data-id="element-990">
                      <div className="space-y-2" data-id="element-991">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-992">
                          Full Name
                        </label>
                        <input type="text" name="senderName" value={formData.senderName} onChange={handleInputChange} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" placeholder="John Doe" data-id="element-993" />
                      </div>
                      <div className="space-y-2" data-id="element-994">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-995">
                          Phone Number
                        </label>
                        <input type="tel" name="senderPhone" value={formData.senderPhone} onChange={handleInputChange} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" placeholder="(555) 123-4567" data-id="element-996" />
                      </div>
                      <div className="space-y-2" data-id="element-997">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-998">
                          Email Address
                        </label>
                        <input type="email" name="senderEmail" value={formData.senderEmail} onChange={handleInputChange} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" placeholder="john@example.com" data-id="element-999" />
                      </div>
                    </div>
                  </motion.div>}

                {/* STEP 2: RECIPIENT */}
                {step === 2 && <motion.div key="step2" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4 sm:space-y-6" data-id="element-1000">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6" data-id="element-1001">
                      Recipient Information
                    </h2>

                    <div className="space-y-4" data-id="element-1002">
                      <div className="space-y-2" data-id="element-1003">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-1004">
                          Full Name
                        </label>
                        <input type="text" name="recipientName" value={formData.recipientName} onChange={handleInputChange} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" placeholder="Jane Doe" data-id="element-1005" />
                      </div>
                      <div className="space-y-2" data-id="element-1006">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-1007">
                          Phone Number
                        </label>
                        <input type="tel" name="recipientPhone" value={formData.recipientPhone} onChange={handleInputChange} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" placeholder="+234 800 000 0000" data-id="element-1008" />
                      </div>
                      <div className="space-y-2" data-id="element-1009">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-1010">
                          Email Address
                        </label>
                        <input type="email" name="recipientEmail" value={formData.recipientEmail} onChange={handleInputChange} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" placeholder="jane@example.com" data-id="element-1011" />
                      </div>
                      <div className="space-y-2" data-id="element-1012">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-1013">
                          Delivery Address (Lagos)
                        </label>
                        <textarea name="deliveryAddress" value={formData.deliveryAddress} onChange={handleInputChange} rows={3} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none resize-none bg-white text-gray-900 text-base" placeholder="Enter complete delivery address..." data-id="element-1014" />
                      </div>
                      <div className="space-y-2" data-id="element-1015">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-1016">
                          Alternate Phone{' '}
                          <span className="text-gray-400 font-normal" data-id="element-1017">
                            (Optional)
                          </span>
                        </label>
                        <input type="tel" name="alternatePhone" value={formData.alternatePhone} onChange={handleInputChange} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" placeholder="+234 800 000 0000" data-id="element-1018" />
                      </div>
                    </div>
                  </motion.div>}

                {/* STEP 3: PACKAGE */}
                {step === 3 && <motion.div key="step3" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4 sm:space-y-6" data-id="element-1019">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6" data-id="element-1020">
                      Package Details
                    </h2>

                    <div className="space-y-4" data-id="element-1021">
                      <div className="space-y-2" data-id="element-1022">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-1023">
                          Package Type
                        </label>
                        <select name="packageType" value={formData.packageType} onChange={handleInputChange} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" data-id="element-1024">
                          <option value="documents" data-id="element-1025">Documents</option>
                          <option value="electronics" data-id="element-1026">Electronics</option>
                          <option value="clothing" data-id="element-1027">Clothing</option>
                          <option value="food" data-id="element-1028">Food Items</option>
                          <option value="other" data-id="element-1029">Other</option>
                        </select>
                      </div>

                      <div className="space-y-2" data-id="element-1030">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-1031">
                          Weight
                        </label>
                        <div className="relative flex rounded-xl border border-gray-300 focus-within:ring-2 focus-within:ring-brand focus-within:border-brand overflow-hidden bg-white" data-id="element-1032">
                          <input type="number" name="weight" value={formData.weight} onChange={handleInputChange} className="w-full px-4 py-3.5 sm:py-3 pr-24 outline-none bg-white text-gray-900 text-base" placeholder="Enter weight..." min="0" step="0.1" data-id="element-1033" />
                          <div className="absolute right-0 top-0 bottom-0 flex" data-id="element-1034">
                            <button type="button" onClick={() => handleUnitToggle('lbs')} className={`px-3 py-3.5 sm:py-3 text-sm font-semibold transition-colors ${formData.weightUnit === 'lbs' ? 'bg-brand text-white' : 'bg-gray-100 text-gray-600 active:bg-gray-200'}`} data-id="element-1035">
                              lbs
                            </button>
                            <button type="button" onClick={() => handleUnitToggle('kg')} className={`px-3 py-3.5 sm:py-3 text-sm font-semibold transition-colors ${formData.weightUnit === 'kg' ? 'bg-brand text-white' : 'bg-gray-100 text-gray-600 active:bg-gray-200'}`} data-id="element-1036">
                              kg
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2" data-id="element-1037">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-1038">
                          Dimensions (inches)
                        </label>
                        <div className="grid grid-cols-3 gap-2 sm:gap-4" data-id="element-1039">
                          <input type="number" name="length" value={formData.length} onChange={handleInputChange} className="w-full px-3 sm:px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" placeholder="L" data-id="element-1040" />
                          <input type="number" name="width" value={formData.width} onChange={handleInputChange} className="w-full px-3 sm:px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" placeholder="W" data-id="element-1041" />
                          <input type="number" name="height" value={formData.height} onChange={handleInputChange} className="w-full px-3 sm:px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" placeholder="H" data-id="element-1042" />
                        </div>
                        <p className="text-xs text-gray-500 flex items-center gap-1" data-id="element-1043">
                          <Info className="w-3 h-3" data-id="element-1044" />
                          Surcharge if any dimension exceeds 20"
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-1045">
                        <div className="space-y-2" data-id="element-1046">
                          <label className="block text-sm font-medium text-gray-700" data-id="element-1047">
                            Quantity
                          </label>
                          <input type="number" name="quantity" value={formData.quantity} onChange={handleInputChange} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" min="1" data-id="element-1048" />
                        </div>
                        <div className="space-y-2" data-id="element-1049">
                          <label className="block text-sm font-medium text-gray-700" data-id="element-1050">
                            Value (USD)
                          </label>
                          <div className="relative" data-id="element-1051">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" data-id="element-1052">
                              $
                            </span>
                            <input type="number" name="value" value={formData.value} onChange={handleInputChange} className="w-full pl-8 pr-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" placeholder="0.00" data-id="element-1053" />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2" data-id="element-1054">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-1055">
                          Special Instructions
                        </label>
                        <textarea name="specialInstructions" value={formData.specialInstructions} onChange={handleInputChange} rows={2} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none resize-none bg-white text-gray-900 text-base" placeholder="Fragile, keep upright, etc..." data-id="element-1056" />
                      </div>

                      <div className="pt-4 border-t border-gray-100" data-id="element-1057">
                        <label className="flex items-start gap-3 cursor-pointer p-3 -m-3 rounded-xl active:bg-gray-50" data-id="element-1058">
                          <input type="checkbox" name="needsInterstate" checked={formData.needsInterstate} onChange={handleInputChange} className="mt-0.5 w-5 h-5 rounded text-brand focus:ring-brand border-gray-300" data-id="element-1059" />
                          <span className="text-sm text-gray-700" data-id="element-1060">
                            Need delivery to another state in Nigeria?
                          </span>
                        </label>

                        {formData.needsInterstate && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} className="mt-4" data-id="element-1061">
                            <select name="destinationState" value={formData.destinationState} onChange={handleInputChange} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" data-id="element-1062">
                              <option value="" className="text-gray-500" data-id="element-1063">
                                Select State...
                              </option>
                              {NIGERIAN_STATES.map(state => <option key={state} value={state} className="text-gray-900" data-id="element-1064">
                                  {state}
                                </option>)}
                            </select>
                          </motion.div>}
                      </div>
                    </div>
                  </motion.div>}

                {/* STEP 4: REVIEW */}
                {step === 4 && <motion.div key="step4" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4 sm:space-y-6" data-id="element-1065">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6" data-id="element-1066">
                      Review & Submit
                    </h2>

                    {/* Sender Summary */}
                    <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-100" data-id="element-1067">
                      <div className="flex justify-between items-center mb-3 sm:mb-4" data-id="element-1068">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm sm:text-base" data-id="element-1069">
                          <User className="w-4 h-4 text-brand" data-id="element-1070" /> Sender
                        </h3>
                        <button onClick={() => setStep(1)} className="text-sm text-brand active:underline p-2 -m-2" data-id="element-1071">
                          Edit
                        </button>
                      </div>
                      <div className="space-y-2 text-sm text-gray-600" data-id="element-1072">
                        <div data-id="element-1073">
                          <span className="text-xs text-gray-400 uppercase" data-id="element-1074">
                            Name:
                          </span>{' '}
                          {formData.senderName || '-'}
                        </div>
                        <div data-id="element-1075">
                          <span className="text-xs text-gray-400 uppercase" data-id="element-1076">
                            Phone:
                          </span>{' '}
                          {formData.senderPhone || '-'}
                        </div>
                        <div data-id="element-1077">
                          <span className="text-xs text-gray-400 uppercase" data-id="element-1078">
                            Drop-off:
                          </span>{' '}
                          {getSelectedLocationName() || '-'}
                        </div>
                      </div>
                    </div>

                    {/* Recipient Summary */}
                    <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-100" data-id="element-1079">
                      <div className="flex justify-between items-center mb-3 sm:mb-4" data-id="element-1080">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm sm:text-base" data-id="element-1081">
                          <MapPin className="w-4 h-4 text-brand" data-id="element-1082" /> Recipient
                        </h3>
                        <button onClick={() => setStep(2)} className="text-sm text-brand active:underline p-2 -m-2" data-id="element-1083">
                          Edit
                        </button>
                      </div>
                      <div className="space-y-2 text-sm text-gray-600" data-id="element-1084">
                        <div data-id="element-1085">
                          <span className="text-xs text-gray-400 uppercase" data-id="element-1086">
                            Name:
                          </span>{' '}
                          {formData.recipientName || '-'}
                        </div>
                        <div data-id="element-1087">
                          <span className="text-xs text-gray-400 uppercase" data-id="element-1088">
                            Phone:
                          </span>{' '}
                          {formData.recipientPhone || '-'}
                        </div>
                        <div data-id="element-1089">
                          <span className="text-xs text-gray-400 uppercase" data-id="element-1090">
                            Address:
                          </span>{' '}
                          {formData.deliveryAddress || '-'}
                        </div>
                      </div>
                    </div>

                    {/* Package Summary */}
                    <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-100" data-id="element-1091">
                      <div className="flex justify-between items-center mb-3 sm:mb-4" data-id="element-1092">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm sm:text-base" data-id="element-1093">
                          <Package className="w-4 h-4 text-brand" data-id="element-1094" /> Package
                        </h3>
                        <button onClick={() => setStep(3)} className="text-sm text-brand active:underline p-2 -m-2" data-id="element-1095">
                          Edit
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm text-gray-600" data-id="element-1096">
                        <div data-id="element-1097">
                          <span className="text-xs text-gray-400 uppercase block" data-id="element-1098">
                            Type
                          </span>{' '}
                          <span className="capitalize" data-id="element-1099">
                            {formData.packageType}
                          </span>
                        </div>
                        <div data-id="element-1100">
                          <span className="text-xs text-gray-400 uppercase block" data-id="element-1101">
                            Weight
                          </span>{' '}
                          {formData.weight} {formData.weightUnit}
                        </div>
                        <div data-id="element-1102">
                          <span className="text-xs text-gray-400 uppercase block" data-id="element-1103">
                            Qty
                          </span>{' '}
                          {formData.quantity}
                        </div>
                        <div data-id="element-1104">
                          <span className="text-xs text-gray-400 uppercase block" data-id="element-1105">
                            Value
                          </span>{' '}
                          ${formData.value || '0'}
                        </div>
                      </div>
                    </div>

                    <label className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100 cursor-pointer active:bg-blue-100" data-id="element-1106">
                      <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-0.5 w-5 h-5 rounded text-brand focus:ring-brand border-gray-300" data-id="element-1107" />
                      <span className="text-sm text-gray-700" data-id="element-1108">
                        I agree to the{' '}
                        <a href="#" className="text-brand underline" data-id="element-1109">
                          Terms and Conditions
                        </a>{' '}
                        and confirm no prohibited items.
                      </span>
                    </label>
                  </motion.div>}
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-100 gap-3" data-id="element-1110">
                {step > 1 ? <Button variant="outline" onClick={prevStep} className="gap-1 sm:gap-2 flex-1 sm:flex-none" data-id="element-1111">
                    <ChevronLeft className="w-4 h-4" data-id="element-1112" />{' '}
                    <span className="hidden sm:inline" data-id="element-1113">Back</span>
                  </Button> : <div data-id="element-1114"></div>}

                {step < 4 ? <Button onClick={nextStep} className="gap-1 sm:gap-2 flex-1 sm:flex-none" data-id="element-1115">
                    <span className="hidden sm:inline" data-id="element-1116">Next Step</span>
                    <span className="sm:hidden" data-id="element-1117">Next</span>{' '}
                    <ChevronRight className="w-4 h-4" data-id="element-1118" />
                  </Button> : <Button onClick={handleProceedToPayment} disabled={!agreedToTerms} className="gap-1 sm:gap-2 flex-1 sm:flex-none" data-id="element-1119">
                    <span className="hidden sm:inline" data-id="element-1120">Proceed to Payment</span>
                    <span className="sm:hidden" data-id="element-1121">Pay</span>{' '}
                    <CreditCard className="w-4 h-4" data-id="element-1122" />
                  </Button>}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - CALCULATOR (Desktop only) */}
          <div className="hidden lg:block lg:col-span-1" data-id="element-1123">
            <div className="sticky top-24" data-id="element-1124">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden" data-id="element-1125">
                <div className="bg-secondary p-4 text-white" data-id="element-1126">
                  <h3 className="font-bold text-lg text-white flex items-center gap-2" data-id="element-1127">
                    <CreditCard className="w-5 h-5 text-white" data-id="element-1128" /> Estimated Cost
                  </h3>
                </div>

                <div className="p-6 space-y-4" data-id="element-1129">
                  <div className="flex justify-between text-sm" data-id="element-1130">
                    <span className="text-gray-600" data-id="element-1131">
                      Base Rate ({formData.packageType})
                    </span>
                    <span className="font-medium" data-id="element-1132">
                      ${costs.base.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1133">
                    <span className="text-gray-600" data-id="element-1134">Weight Charge</span>
                    <span className="font-medium" data-id="element-1135">
                      ${costs.weight.toFixed(2)}
                    </span>
                  </div>
                  {costs.surcharge > 0 && <div className="flex justify-between text-sm text-warning" data-id="element-1136">
                      <span className="flex items-center gap-1" data-id="element-1137">
                        <AlertCircle className="w-3 h-3" data-id="element-1138" /> Oversize Surcharge
                      </span>
                      <span className="font-medium" data-id="element-1139">
                        ${costs.surcharge.toFixed(2)}
                      </span>
                    </div>}
                  {costs.interstate > 0 && <div className="flex justify-between text-sm" data-id="element-1140">
                      <span className="text-gray-600" data-id="element-1141">Interstate Delivery</span>
                      <span className="font-medium" data-id="element-1142">
                        ${costs.interstate.toFixed(2)}
                      </span>
                    </div>}
                  <div className="pt-4 border-t border-gray-100 flex justify-between items-end" data-id="element-1143">
                    <span className="text-gray-900 font-bold" data-id="element-1144">
                      Estimated Total
                    </span>
                    <span className="text-2xl font-bold text-brand" data-id="element-1145">
                      ${costs.total.toFixed(2)}
                    </span>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-xs text-blue-700 flex gap-2" data-id="element-1146">
                    <Info className="w-4 h-4 flex-shrink-0" data-id="element-1147" />
                    Final price confirmed after package review.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Price Bar */}
      <MobilePriceBar data-id="element-1148" />

      {/* Location Modal */}
      <LocationModal data-id="element-1149" />
    </>;
}